// src/modules/app-views/invoices/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import InvoicesInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import invoicesInitActionButton from './pages/Init/actions/actionButton';
import invoicesInitMenuButton from './pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import InvoicesDocument from './pages/Document';
import invoicesDocumentActionButton from './pages/Document/actions/actionButton';
import invoicesDocumentDropdownMenu from './pages/Document/actions/menuButton';
import invoicesDocumentDecorateRequest from './pages/Document/decorators/request';
import CardHeaderInvoiceNumberDateTotal from 'modules/app-views/invoices/components/CardHeaderInvoiceNumberDateTotal';
import CardHeaderInvoiceTitle from 'modules/app-views/invoices/components/CardHeaderInvoiceTitle';
import CardHeaderInvoiceOperationOrderSeller from 'modules/app-views/invoices/components/CardHeaderInvoiceOperationOrderSeller';
import CardHeaderInvoiceIssuerCnpjIe from 'modules/app-views/invoices/components/CardHeaderInvoiceIssuerCnpjIe';
import CardHeaderInvoiceSubtitle from 'modules/app-views/invoices/components/CardHeaderInvoiceSubtitle';
import CardHeaderInvoiceTags from 'modules/app-views/invoices/components/CardHeaderInvoiceTags';
import InvoicePreviewHeader from 'modules/app-views/invoices/components/InvoicePreviewHeader';
import InvoicePreviewBody from 'modules/app-views/invoices/components/InvoicePreviewBody';
import InvoicePreviewActions from 'modules/app-views/invoices/components/InvoicePreviewActions';
import { isLockedStatus } from 'services/fiscalService';
import InvoiceFilterDrawerBody from './components/InvoiceFilterDrawerBody';
import InvoicesToolbarLeft from './components/InvoicesToolbarLeft';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'invoices';

// *******************************************************************************
// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 1,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: null,
  parametersSeed: null,

  // ───────────────────────────────────────────────────────────────────────
  // MIGRATORY
  // ───────────────────────────────────────────────────────────────────────
  migratory: {
    title: 'invoice_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <InvoicesInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.INVOICES.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [
        { field: '_metadata.audit.updatedAt', order: 'desc', index: 1 },
      ],
      search: {},
      textSearch: '',
      filter: {},
      drawer: {
        folder: 'inbox',
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['line', 'createdAt'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      filterByCompany: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actionsInContent: true,
      leftContent: <InvoicesToolbarLeft />,
      actions: ['home', 'refresh', invoicesInitActionButton, invoicesInitMenuButton],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // FILTER DRAWER
    // ───────────────────────────────────────────────────────────────────────
    filterDrawer: {
      folderSection: {
        title: 'places',
        inboxLabel: 'assets',
        archiveLabel: 'archived_items',
        recycleLabel: 'deleted_items',
      },
      components: {
        body: props => <InvoiceFilterDrawerBody {...props} />,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // PREVIEW DRAWER
    // ───────────────────────────────────────────────────────────────────────
    preview: {
      actions: ['edit', 'archive', 'remove'],
      showCardHeader: false, // We use custom header component instead
      noDividerAfterHeader: true, // Header has its own background styling
      components: {
        title: 'document_preview',
        header: props => <InvoicePreviewHeader {...props} />,
        body: props => <InvoicePreviewBody {...props} />,
        actions: props => <InvoicePreviewActions {...props} />,
        footer: null,
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <InvoicesDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.INVOICES.URL,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: invoicesDocumentDecorateRequest,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {
        // Default: NFe (most common for business)
        documentType: 'nfe',
        modelo: 55, // 55=NFe, 65=NFCe (SEFAZ model code)
        operationType: 1, // Exit (sale)
        finalConsumer: 1, // Yes - final consumer
        environment: 1, // Production
        consumerPresence: 1, // In-person (Presencial)
        purpose: 1, // Normal (1=Normal, 2=Complementary, 3=Adjustment, 4=Return)
        natureOfOperation: 'Venda',
        issueDate: new Date(),
        intermediation: 0, // Own operation
        freightModality: 9, // No freight (Sem frete)
        items: [],
        payment: {
          indicator: 0, // Cash
          methods: [],
        },
        // NFe B2B recipient defaults
        recipient: {
          indIEDest: 9, // Default: Non-contributor (1=Contributor, 2=Exempt, 9=Non-contributor)
        },
      },
      actions: [
        // EditRefresh - disabled for locked statuses (SEFAZ processed documents)
        {
          action: 'editRefresh',
          disabled: ({ page }) => isLockedStatus(page?.data?.status),
        },
        invoicesDocumentActionButton(),
        invoicesDocumentDropdownMenu(),
        'return',
      ],
      // viewMode configuration:
      // - viewModeOnOpen: 'edit' | 'view' - how existing documents open (default: 'view' = locked)
      // - viewModeAfterSave: 'edit' | 'view' - mode after save (default: 'view' = lock after save)
      viewModeOnOpen: 'view', // invoices open in edit mode (editable)
      viewModeAfterSave: 'view', // after save, stay in edit mode
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: true,
        placeholder: 'qrcode',
        noShowInitials: true,
        noShowColor: true,
        hideToolbar: true,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        // showHighlightTitle: () => {
        //   const storedCurrentPage = useZustand?.('module', 'currentPage', false);
        //   return storedCurrentPage?.data?.id
        //     ? translateX('fiscal_document')
        //     : translateX('new_fiscal_document');
        // },
        // showHighlightIcon: <FileTextOutlined />,
      },

      card: {
        title: () => <CardHeaderInvoiceTitle />,
        subtitle: () => <CardHeaderInvoiceSubtitle />,

        column1: props => <CardHeaderInvoiceNumberDateTotal {...props} forceShow={true} />,
        column2: props => <CardHeaderInvoiceOperationOrderSeller {...props} forceShow={true} />,
        column3: props => <CardHeaderInvoiceIssuerCnpjIe {...props} forceShow={true} />,

        tags: props => <CardHeaderInvoiceTags form={props?.form} />,
      },
    },
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  document: {
    documentType: 'nfce',
    operationType: 1,
    purpose: 1,
    consumerPresence: 1,
    items: [],
    payment: {
      indicator: 0,
      methods: [],
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };
